/* API response for GET /api/investments/:accountId/transactions */
export interface GetTransactionsResponse {
    transactions: ApexActivity[];
    pendingTransactions: PendingApexAccountTransaction[];
}

export type PendingApexAccountTransaction =
    | PendingIctDepositTransaction
    | PendingIctWithdrawalRequestTransaction
    | PendingCashJournalTransaction;

export interface PendingIctDepositTransaction {
    kind: 'ict_deposit';
    idempotencyKey: string;
    amountCents: number;
    status: string;
    expectedSettlementDate: string | null;
    memo: string | null;
    createdAt: string;
    statusUpdatedAt: string;
}

export interface PendingIctWithdrawalRequestTransaction {
    kind: 'ict_withdrawal_request';
    id: string;
    amountCents: number | null;
    fullDisbursement: boolean;
    status: string;
    createdAt: string;
    statusUpdatedAt: string;
}

export interface PendingCashJournalTransaction {
    kind: 'cash_journal';
    clientTransferId: string;
    direction: 'OUTBOUND' | 'INBOUND';
    amountCents: number | null;
    fullDisbursement: boolean | null;
    orchestrationStatus: string;
    apexCurrentState: string | null;
    purpose: string;
    counterpartyApexAccountId: string;
    createdAt: string;
    statusUpdatedAt: string;
}

export interface ApexWithholding {
    amount: { value: string };
    rate: { value: string } | null;
    state: string;
    taxYear: number;
    type: string;
}

export interface ApexWithdrawal {
    additionalInstructions: string;
    advance: boolean;
    closingAccount: boolean;
    conversion: boolean;
    destinationAccountNumber: string;
    destinationInstitution: string;
    distributionType: string;
    distributionYear: number;
    fedReferenceNumber: string;
    futuresSettlement: boolean;
    micro: boolean;
    originatingInstitution: string;
    periodic: boolean;
    retirementType: string;
    taxable: boolean;
    totalDistribution: boolean;
    type: string;
}

export interface ApexActivity {
    accountId: string;
    accountMemo: string;
    accruedInterest: [];
    activityDate: {
        day: number;
        month: number;
        year: number;
    };
    activityDescription: string;
    activityId: string;
    activityTime: string;
    assetDescription: string;
    assetId: string;
    commissions: [];
    correspondentId: string;
    creationTime: string;
    currencyAssetId: string;
    currencyCode: string;
    deposit: {
        additionalInstructions: string;
        contributionType: string;
        contributionYear: number;
        fedReferenceNumber: string;
        originatingAccountNumber: string;
        type: string;
    };
    fees: [];
    generation: string;
    grossAmount: {
        value: string;
    };
    name: string;
    netAmount: {
        value: string;
    };
    nextActivityId: string;
    nextActivityProcessDate: null;
    previousActivityId: string;
    previousProcessDate: null;
    price: {
        value: string;
    };
    processDate: {
        day: number;
        month: number;
        year: number;
    };
    quantity: {
        value: string;
    };
    regionCode: string;
    roundingAmount: null;
    roundingReason: string;
    settleDate: {
        day: number;
        month: number;
        year: number;
    };
    side: string;
    sideModifier: string;
    state: string;
    sweep: {
        action: string;
        type: string;
        eod_redemption: boolean;
    };
    credit: {
        creditType: string;
        taxable: boolean;
        additionalInstructions: string;
    };
    subtypeCategory: string;
    type: string;
    withholdings: ApexWithholding[];
    withdrawal?: ApexWithdrawal;
    accountTransfer: {
        action: string;
        acatsControlNumber: string;
        institution: string;
        additionalInstructions: string;
        acatsAssetSequenceNumber: string;
        contraPartyId: string;
        contraPartyAccountNumber: string;
        method: string;
        accountTransferType: string;
        fairMarketValue: { value: string };
        fairMarketValueDate: {
            day: number;
            month: number;
            year: number;
        };
        giftTransfer: false;
    };
    assetSymbol: string | null | undefined;
}

export const formatApexDate = ({
    day,
    month,
    year,
}: {
    day: number;
    month: number;
    year: number;
}) => {
    return `${month}/${day}/${year}`;
};
